/**
 * Client-generatable, time-sortable identifiers.
 *
 * why: ODR devices create records while offline (checklist runs, answers, problem
 * tags). If the *server* minted primary keys we would need dedup heuristics on
 * sync, and a retried request would create a second Problem Tag. Instead the
 * device mints the id up front and the server treats sync as an idempotent
 * "insert if absent" keyed on that id. Retry safety falls out of the data model
 * rather than being bolted on. See docs/DECISIONS.md ADR-0001.
 *
 * Format: ULID-compatible — 10 chars of millisecond timestamp + 16 chars of
 * randomness, Crockford base32, lexicographically sortable by creation time.
 */

const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'; // Crockford base32 (no I, L, O, U)
const ENCODING_LEN = ENCODING.length;
const TIME_LEN = 10;
const RANDOM_LEN = 16;

/** Uppercase Crockford base32, 26 chars. */
export const ID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;

function randomBytes(size: number): Uint8Array {
  const buf = new Uint8Array(size);
  // Available in browsers and Node >= 19 as a global.
  globalThis.crypto.getRandomValues(buf);
  return buf;
}

function encodeTime(now: number): string {
  let out = '';
  let t = now;
  for (let i = TIME_LEN - 1; i >= 0; i--) {
    out = ENCODING[t % ENCODING_LEN] + out;
    t = Math.floor(t / ENCODING_LEN);
  }
  return out;
}

function encodeRandom(): string {
  const bytes = randomBytes(RANDOM_LEN);
  let out = '';
  for (let i = 0; i < RANDOM_LEN; i++) {
    // Mask into the 32-symbol alphabet. Slight modulo bias is irrelevant here:
    // 80 bits of entropy per id, and ids are also namespaced by table.
    out += ENCODING[bytes[i]! % ENCODING_LEN];
  }
  return out;
}

let lastTime = 0;
let lastRandom = '';

/**
 * Generate a new id. Monotonic within the same millisecond so that ids created
 * in a tight loop (e.g. seeding a 17-item checklist) still sort in insert order.
 */
export function newId(atMs?: number): string {
  const now = atMs ?? Date.now();
  if (now === lastTime) {
    lastRandom = incrementBase32(lastRandom);
  } else {
    lastTime = now;
    lastRandom = encodeRandom();
  }
  return encodeTime(now) + lastRandom;
}

function incrementBase32(str: string): string {
  const chars = str.split('');
  for (let i = chars.length - 1; i >= 0; i--) {
    const idx = ENCODING.indexOf(chars[i]!);
    if (idx < ENCODING_LEN - 1) {
      chars[i] = ENCODING[idx + 1]!;
      return chars.join('');
    }
    chars[i] = ENCODING[0]!;
  }
  // Overflowed a full 16-char random block in one millisecond — practically
  // impossible, but fall back to a fresh block rather than returning a dup.
  return encodeRandom();
}

export function isValidId(value: unknown): value is string {
  return typeof value === 'string' && ID_PATTERN.test(value);
}

/** Extract the creation timestamp encoded in an id. */
export function idTimestamp(id: string): number {
  let t = 0;
  for (let i = 0; i < TIME_LEN; i++) {
    t = t * ENCODING_LEN + ENCODING.indexOf(id[i]!);
  }
  return t;
}
